import type { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import { Grid } from '@/components/Grid';
import ServerSidePagination from '@/components/Grid/Pagination/ServerSidePagination';
import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import { CREATIVES_ENDPOINT, LINE_ITEMS_ENDPOINT } from '@/constants/endpoints';
import CreativesModalButtons from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalButtons';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Modal, TextField } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import axios from 'axios';
import debounce from 'debounce';
import { ChangeEvent, FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { type ILineItemRow, columns } from './columns';

interface IBulkAssignLineItemsModalProps {
  open: boolean;
  handleClose: () => void;
  creative: ICreative;
}

const BulkAssignLineItemsModal = memo(({ open, handleClose, creative }: IBulkAssignLineItemsModalProps) => {
  const [lineItems, setLineItems] = useState<ILineItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchParam, setSearchParam] = useState('');
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const initialSelectionDoneRef = useRef(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 15 });
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchLineItems = useCallback(async () => {
    if (!creative?.advertiser?.id) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const { data } = await axios.get(LINE_ITEMS_ENDPOINT, {
        params: {
          advertiserId: creative.advertiser.id,
          creativeId: creative.id,
          search: searchParam || undefined,
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        },
        signal: controller.signal,
      });
      setLineItems(data.data);
      setRowCount(data.meta?.total ?? data.data.length);

      // Pre-select already-associated line items on first load
      if (!initialSelectionDoneRef.current) {
        const associatedIds = data.data
          .filter((li: ILineItemRow & { isAssociated?: boolean }) => li.isAssociated)
          .map((li: ILineItemRow) => li.id);
        if (associatedIds.length) {
          setSelectedIds(associatedIds);
        }
        initialSelectionDoneRef.current = true;
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error('Failed to load line items');
      }
    } finally {
      setLoading(false);
    }
  }, [creative?.advertiser?.id, creative?.id, searchParam, paginationModel]);

  useEffect(() => {
    if (open) {
      fetchLineItems();
    }
  }, [open, fetchLineItems]);

  useEffect(() => {
    if (!open) {
      setLineItems([]);
      setSearchValue('');
      setSearchParam('');
      setSelectedIds([]);
      initialSelectionDoneRef.current = false;
      setPaginationModel({ page: 0, pageSize: 15 });
    }
  }, [open]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchParam(value);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }, 500),
    [],
  );

  useEffect(() => {
    return () => debouncedSearch.clear();
  }, [debouncedSearch]);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newSearch = e.target.value;
      setSearchValue(newSearch);
      debouncedSearch(newSearch);
    },
    [debouncedSearch],
  );

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setSubmitting(true);
      try {
        const { data } = await axios.post(`${CREATIVES_ENDPOINT}/${creative.id}/bulk-assign-line-items`, {
          lineItemIds: selectedIds,
        });

        const messages: string[] = [];
        if (data.assigned.length) messages.push(`Assigned to ${data.assigned.length} line item(s)`);
        if (data.unassigned.length) messages.push(`Removed from ${data.unassigned.length} line item(s)`);
        if (data.skipped.length) messages.push(`${data.skipped.length} unchanged`);
        if (data.failed.length) messages.push(`${data.failed.length} failed`);

        if (data.failed.length && !data.assigned.length && !data.unassigned.length) {
          toast.error(messages.join('. '));
        } else {
          toast.success(messages.join('. '));
        }

        handleClose();
      } catch {
        toast.error('Failed to update creative assignments');
      } finally {
        setSubmitting(false);
      }
    },
    [selectedIds, creative.id, handleClose],
  );

  return (
    <Modal open={open}>
      <ModalBody className='!w-[80%] max-w-[1100px]'>
        <div className='flex items-center justify-between mb-4'>
          <p className='pl-4 pt-2 text-xl font-semibold'>Bulk Assign to Line Items</p>
          <div className='absolute right-[24px] top-[24px]'>
            <CloseButton onClick={handleClose} />
          </div>
        </div>

        <div className='px-4 mb-4'>
          <TextField
            fullWidth
            size='small'
            placeholder='Search line items...'
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <Grid
            loading={loading}
            checkboxSelection
            rows={lineItems}
            columns={columns}
            columnHeaderHeight={40}
            rowHeight={50}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            pagination
            rowSelection
            onRowSelectionModelChange={setSelectedIds}
            rowSelectionModel={selectedIds}
            showCellVerticalBorder
            showColumnVerticalBorder
            disableCellSelection
            slots={{
              // @ts-expect-error
              pagination: ServerSidePagination,
              columnSortedAscendingIcon: KeyboardArrowUp,
              columnSortedDescendingIcon: KeyboardArrowDown,
            }}
            scrollbarSize={0}
            slotProps={{
              pagination: {
                // @ts-expect-error doesn't recognize rowCount
                rowCount,
                loading,
              },
            }}
            rowCount={rowCount}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
          />
          <CreativesModalButtons
            handleClose={handleClose}
            loading={submitting}
            isSaveDisabled={false}
            saveButtonTitle='Save'
            saveButtonLoadingTitle='Saving...'
          />
        </form>
      </ModalBody>
    </Modal>
  );
});

export default BulkAssignLineItemsModal;
